import React from 'react';
import { Box, Divider, Theme, Typography } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { css } from '@emotion/css';
import CanvasCardFrame, { CanvasCardDragSharedProps } from './CanvasCardFrame';
import type { AnchorLineSpec } from '../utils/canvasAnchorUtils';
import { RepositoryEntriesRowDto, useGetRepositoryByIdQuery, useLoadRepositoryEntry3Query } from '../services/repositoryEntries';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import {
	selectAllEntryProperties,
	selectAllPropertyDefinitions,
	selectAllRepositories,
	selectAllRepositoryEntries,
} from '../containers/contentSlice';
import { entryPropertyGetReadableValue, entryPropertyGetSecondaryValue, populateRepositoryEntriesForTable, sortEntryByRank } from '../common/util';
import { PropertyDefinition } from '../types';

export interface CanvasRepositoryEntryCardProps extends CanvasCardDragSharedProps {
	repositoryId: string;
	repositoryEntryId: string;
	attachedAnchorLines?: AnchorLineSpec[];
	zIndex?: number;
}

export const REFERENCE_CARD_SX: SystemStyleObject<Theme> = {
	maxWidth: 260,
	borderRadius: 6,
	boxShadow: (theme) => theme.palette.mode === 'light'
		? '0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1), 0 -2px 5px rgba(0,0,0,0.06)'
		: '0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1), 0 -2px 5px rgba(0,0,0,0.12)',
};

export interface RepositoryEntryCardBodyProps {
	repositorySingular?: string;
	collectionColor?: string;
	propertyDefinitions: PropertyDefinition[];
	row?: RepositoryEntriesRowDto;
}

export function RepositoryEntryCardBody({
	repositorySingular,
	collectionColor,
	propertyDefinitions,
	row,
}: RepositoryEntryCardBodyProps) {
	const classes = {
		maxHeight2Lines: css`
			overflow: hidden;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
		`,
		maxHeight3Lines: css`
			overflow: hidden;
			display: -webkit-box;
			-webkit-line-clamp: 3;
			-webkit-box-orient: vertical;
		`,
	};
	const firstDef = propertyDefinitions[0];
	const secondDefs = propertyDefinitions.slice(1, 3);
	const entryTitle = firstDef ? entryPropertyGetReadableValue(firstDef, row?.data?.[firstDef.slug]) : 'Untitled';
	const entrySecondaryTitle = firstDef ? entryPropertyGetSecondaryValue(firstDef, row?.data?.[firstDef.slug]) : null;
	const isArchived = Boolean(row?.isArchived);

	return (
		<>
			<Box display="flex" alignItems="center" gap={0.5} sx={{textAlign: 'right', position: 'absolute', bottom: 0, right: 12}}>
				<Typography textAlign="center" color="action.disabled" variant="caption" sx={{ fontSize: '0.625rem', lineHeight: 1.5}}>{repositorySingular ?? ''}</Typography>
				{collectionColor && <Box sx={{ mt: -1, width: 6, height: 6, borderRadius: '50%', backgroundColor: collectionColor }} />}
			</Box>
			<Box display="flex" flexDirection="column" gap={1} sx={{ minWidth: 0 }}>
				<Box sx={{ overflow: 'hidden', wordBreak: 'break-word' }}>
					<Typography
						mt={0.25}
						variant="h6"
						color="text.primary"
						sx={{ textDecoration: isArchived ? 'line-through' : 'none' }}
						className={classes.maxHeight3Lines} 
					>
						{entryTitle || 'Untitled'}
					</Typography>
					{entrySecondaryTitle && (
						<Typography variant="caption" color="primary.light" mt={-1.5}>
							{entrySecondaryTitle}
						</Typography>
					)}
				</Box>
				{secondDefs.length > 0 && <Divider />}
				{secondDefs.map((def) => {
					const primaryValue = entryPropertyGetReadableValue(def, row?.data?.[def.slug]);
					const secondaryValue = entryPropertyGetSecondaryValue(def, row?.data?.[def.slug]);
					return (
						<Box key={def.slug} display="flex" flexDirection="column" gap={0.25}>
							<Typography variant="caption" sx={{ color: 'text.secondary' }}>
								{def.label}
							</Typography>
							<Typography variant="body2" className={classes.maxHeight2Lines} sx={{ color: 'text.primary' }}>
								{primaryValue || '-'}
							</Typography>
							{secondaryValue && (
								<Typography variant="caption" color="primary.light">
									{secondaryValue}
								</Typography>
							)}
						</Box>
					);
				})}
			</Box>
		</>
	);
}

export function CanvasRepositoryEntryCard({
	position,
	isSelected,
	scale,
	collectionColor,
	isPositionLocked,
	onPositionChange,
	constrainPosition,
	getDragTilt,
	onDragStart,
	onDragActivate,
	onDragMove,
	onDragEnd,
	cardNodeRef,
	onFocus,
	onBlur,
	onSelect,
	repositoryId,
	repositoryEntryId,
	attachedAnchorLines,
	zIndex,
}: CanvasRepositoryEntryCardProps) {
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const allRepositories = useAppSelector(selectAllRepositories);
	const allRepositoryEntries = useAppSelector(selectAllRepositoryEntries);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const allEntryProperties = useAppSelector(selectAllEntryProperties);

	const { data: repositoryById } = useGetRepositoryByIdQuery(repositoryId, {
		skip: !isAuthenticated || !repositoryId,
	});
	const { data: repositoryEntryBundle } = useLoadRepositoryEntry3Query(repositoryEntryId, {
		skip: !isAuthenticated || !repositoryEntryId,
	});
	const repository = React.useMemo(() => {
		if (isAuthenticated) {
			return repositoryById?.repository;
		}
		return allRepositories.find((r) => r.id === repositoryId);
	}, [repositoryById?.repository, isAuthenticated, allRepositories, repositoryId]);

	const propertyDefinitions = React.useMemo(() => {
		const defs = isAuthenticated
			? (repositoryById?.propertyDefinitions ?? [])
			: allPropertyDefinitions.filter((def) => def.parentId === repositoryId);
		return [...defs].sort(sortEntryByRank) as PropertyDefinition[];
	}, [allPropertyDefinitions, isAuthenticated, repositoryById?.propertyDefinitions, repositoryId]);
	const row = React.useMemo(() => {
		if (isAuthenticated) {
			return repositoryEntryBundle?.rows?.[0];
		}
		const repository = allRepositories.find((r) => r.id === repositoryId);
		if (!repository) return undefined;
		return populateRepositoryEntriesForTable(
			repository,
			allRepositoryEntries,
			allPropertyDefinitions,
			allEntryProperties
		).find((entry) => entry.id === repositoryEntryId);
	}, [
		allEntryProperties,
		allPropertyDefinitions,
		allRepositories,
		allRepositoryEntries,
		isAuthenticated,
		repositoryEntryBundle?.rows,
		repositoryEntryId,
		repositoryId,
	]);
	const anchorDecorations = attachedAnchorLines?.length ? (
		<>
			{attachedAnchorLines.map((line) => (
				<React.Fragment key={line.anchorId}>
					<Box
						aria-hidden
						sx={{
							position: 'absolute',
							left: '50%',
							top: '50%',
							pointerEvents: 'none',
							zIndex: -1,
							...(line.direction === 'vertical'
								? {
									height: line.max - line.min,
									borderLeft: (theme: any) => `${line.isActive ? 2 : 1}px dashed ${theme.palette.primary.main}`,
								}
								: {
									width: line.max - line.min,
									borderTop: (theme: any) => `${line.isActive ? 2 : 1}px dashed ${theme.palette.primary.main}`,
								}),
							opacity: line.isActive ? 0.9 : 0.45,
							animation: line.isActive ? 'anchorLinePulse 0.9s ease-in-out infinite' : 'none',
							'@keyframes anchorLinePulse': {
								'0%': { opacity: 0.55 },
								'50%': { opacity: 1 },
								'100%': { opacity: 0.55 },
							},
						}}
					/>
				</React.Fragment>
			))}
		</>
	) : undefined;

	return (
		<CanvasCardFrame
			position={position}
			scale={scale}
			isSelected={isSelected}
			collectionColor={collectionColor}
			isPositionLocked={isPositionLocked}
			onPositionChange={onPositionChange}
			constrainPosition={constrainPosition}
			getDragTilt={getDragTilt}
			onDragStart={onDragStart}
			onDragActivate={onDragActivate}
			onDragMove={onDragMove}
			onDragEnd={onDragEnd}
			cardNodeRef={cardNodeRef}
			onFocus={onFocus}
			onBlur={onBlur}
			onSelect={onSelect}
			ariaLabel={'Reference'}
			maxWidth
			sx={REFERENCE_CARD_SX}
			decorations={anchorDecorations}
			zIndex={zIndex}
		>
			<RepositoryEntryCardBody
				repositorySingular={repository?.singular ?? undefined}
				collectionColor={collectionColor}
				propertyDefinitions={propertyDefinitions}
				row={row}
			/>
		</CanvasCardFrame>
	);
}

/** Memoized so panning/zooming (which re-renders CanvasView every frame) doesn't also re-render every reference card — CanvasView keeps this card's props referentially stable across those frames specifically so this bail-out holds. */
export default React.memo(CanvasRepositoryEntryCard);
